import { useState, useEffect, useCallback } from 'react';
import { CreditCard, CheckCircle, AlertCircle, ExternalLink, Loader, Smartphone, Sparkles, X, ChevronLeft, Building2, MapPin, Phone, Mail, Globe, FileText, ArrowRight, Unlink } from 'lucide-react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { PLATFORM_FEE_PERCENT, PLATFORM_FEE_PERCENT_BNPL } from './stripe';
import { useModal } from './AppModal';
import ReaderManagement from './ReaderManagement';
import { useTheme } from './ThemeContext';
import { ConnectAccountOnboarding, ConnectComponentsProvider } from '@stripe/react-connect-js';
import { loadConnectAndInitialize } from '@stripe/connect-js';

const BUSINESS_TYPE_OPTIONS = [
  { value: 'sole_proprietor', label: 'Sole Proprietor' },
  { value: 'llc', label: 'LLC' },
  { value: 'corporation', label: 'Corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'nonprofit', label: 'Non-profit' },
];

// Map our business_type to Stripe's business_type
const STRIPE_BUSINESS_TYPE_MAP = {
  sole_proprietor: 'individual',
  llc: 'company',
  corporation: 'company',
  partnership: 'company',
  nonprofit: 'non_profit',
};

// MCC codes for common field service businesses
const MCC_MAP = {
  garage_door: '1740',
  hvac: '1711',
  plumbing: '1711',
  electrical: '1731',
  pest_control: '7342',
  carpet_cleaning: '7217',
  pressure_washing: '7349',
  mobile_detailing: '7542',
  landscaping: '0780',
  roofing: '1761',
  painting: '1721',
  general_contractor: '1520',
  locksmith: '7699',
  appliance_repair: '7623',
  pool_service: '1799',
  tree_service: '0783',
  cleaning: '7349',
  handyman: '1520',
  other: '1799'
};

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
];

const EMPTY_FORM = {
  businessName: '',
  businessType: 'sole_proprietor',
  industry: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  email: '',
  url: 'https://workvanapp.com',
  description: '',
};

export default function PaymentSettings({ companyId }) {
  const { colors } = useTheme();
  const { showError, showSuccess } = useModal();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [stripeStatus, setStripeStatus] = useState({
    connected: false,
    accountId: null,
    chargesEnabled: false,
    payoutsEnabled: false,
    detailsSubmitted: false
  });
  const [stats, setStats] = useState({
    totalCollected: 0,
    platformFees: 0,
    pendingPayouts: 0
  });
  const [processingFeeEnabled, setProcessingFeeEnabled] = useState(false);
  const [defaultFeePercent, setDefaultFeePercent] = useState(3.0);
  const [showEmbeddedOnboarding, setShowEmbeddedOnboarding] = useState(false);
  const [connectInstance, setConnectInstance] = useState(null);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0); // 0=closed, 1=form, 2=review, 3=stripe
  const [businessForm, setBusinessForm] = useState({ ...EMPTY_FORM });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [squareStatus, setSquareStatus] = useState({
    connected: false,
    merchantId: null,
    businessName: null,
    locationId: null,
    connectedAt: null,
  });
  const [paymentProcessor, setPaymentProcessor] = useState('none');
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  const PK_LIVE = 'pk_live_51ShJBYJJDak9TtOoaqll0xNqwEsI0nnGNUKrX4HIk1hGgRatwxGYG1UHjk9jMUh3w3Ez9OJt5JO0kyPpOHBqBfbS00AHoP3nbL';
  const PK_TEST = 'pk_test_51ShJBjJeNvIPxmGw3dvhBsGj4EdthiDRNwhpbTOr9EUmVqVyxC5sZLo9wZo1hbfIBo2Sx2yLrgyM3s1wQClcgLCo00lwb2Ldjh';

  useEffect(() => {
    if (companyId) {
      loadStripeStatus();
    }

    // Check for Square OAuth callback result in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const squareResult = urlParams.get('square');
    if (squareResult === 'success') {
      showSuccess('Square connected successfully!');
      // Clean up URL params
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    } else if (squareResult === 'error') {
      const reason = urlParams.get('reason') || 'unknown';
      showError(`Square connection failed: ${reason.replace(/_/g, ' ')}`);
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    }
  }, [companyId]); // eslint-disable-line

  const loadStripeStatus = async () => {
    try {
      setLoading(true);

      const { data: company, error } = await supabaseData
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (error) throw error;

      setIsDemo(!!company?.is_demo);
      setStripeStatus({
        connected: !!company?.stripe_connect_id,
        accountId: company?.stripe_connect_id,
        chargesEnabled: company?.stripe_charges_enabled || false,
        payoutsEnabled: company?.stripe_payouts_enabled || false,
        detailsSubmitted: company?.stripe_connect_status === 'complete' || (company?.stripe_charges_enabled && company?.stripe_payouts_enabled)
      });

      setProcessingFeeEnabled(company?.processing_fee_enabled || false);
      setDefaultFeePercent(company?.default_processing_fee_percent || 3.0);

      // Load Square status
      setSquareStatus({
        connected: !!company?.square_merchant_id,
        merchantId: company?.square_merchant_id,
        businessName: null, // TODO: fetch from Square API when credentials are available
        locationId: company?.square_location_id,
        connectedAt: company?.square_connected_at,
      });
      setPaymentProcessor(company?.payment_processor || 'none');

      if (company?.stripe_connect_id) {
        await loadPaymentStats();
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentStats = async () => {
    try {
      const { data: payments, error } = await supabaseData
        .from('payments')
        .select('amount, platform_fee, status')
        .eq('company_id', companyId);

      if (error) {
        setStats({ totalCollected: 0, platformFees: 0, pendingPayouts: 0 });
        return;
      }

      if (payments) {
        const completed = payments.filter(p => p.status === 'succeeded');
        const pending = payments.filter(p => p.status === 'pending');

        setStats({
          totalCollected: completed.reduce((sum, p) => sum + (p.amount || 0), 0),
          platformFees: completed.reduce((sum, p) => sum + (p.platform_fee || 0), 0),
          pendingPayouts: pending.reduce((sum, p) => sum + (p.amount || 0), 0)
        });
      }
    } catch (error) {
      setStats({ totalCollected: 0, platformFees: 0, pendingPayouts: 0 });
    }
  };

  // Pre-fill the business form from company data
  const prefillFormFromCompany = async () => {
    try {
      const { data: company } = await supabaseData
        .from('companies')
        .select('name, business_type, quote_company_email, quote_company_phone, quote_company_address, quote_company_city, quote_company_state, quote_company_zip')
        .eq('id', companyId)
        .single();

      if (company) {
        // Map DB business_type to our form options
        let formBizType = 'sole_proprietor';
        const bt = (company.business_type || '').toLowerCase();
        if (bt.includes('llc')) formBizType = 'llc';
        else if (bt.includes('corp')) formBizType = 'corporation';
        else if (bt.includes('partner')) formBizType = 'partnership';
        else if (bt.includes('nonprofit') || bt.includes('non-profit') || bt.includes('non_profit')) formBizType = 'nonprofit';

        setBusinessForm(prev => ({
          ...prev,
          businessName: company.name || '',
          businessType: formBizType,
          industry: company.business_type || '',
          street: company.quote_company_address || '',
          city: company.quote_company_city || '',
          state: company.quote_company_state || '',
          zip: company.quote_company_zip || '',
          phone: company.quote_company_phone || '',
          email: company.quote_company_email || '',
          url: 'https://workvanapp.com',
          description: '',
        }));
      }
    } catch (e) {}
  };

  // AI Autofill - fills ALL fields
  const handleAiAutofill = async () => {
    setAiLoading(true);
    try {
      const resp = await fetch('/api/ai-business-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId })
      });
      const data = await resp.json();
      if (data.success && data.profile) {
        const p = data.profile;
        let formBizType = 'sole_proprietor';
        const bt = (p.business_type || '').toLowerCase();
        if (bt.includes('llc')) formBizType = 'llc';
        else if (bt.includes('corp')) formBizType = 'corporation';
        else if (bt.includes('partner')) formBizType = 'partnership';
        else if (bt.includes('nonprofit') || bt.includes('non-profit') || bt.includes('non_profit')) formBizType = 'nonprofit';

        setBusinessForm({
          businessName: p.business_name || businessForm.businessName,
          businessType: formBizType,
          industry: p.business_type || businessForm.industry,
          street: p.address || businessForm.street,
          city: p.city || businessForm.city,
          state: p.state || businessForm.state,
          zip: p.zip || businessForm.zip,
          phone: p.phone || businessForm.phone,
          email: p.email || businessForm.email,
          url: p.url || 'https://workvanapp.com',
          description: p.description || '',
        });
      }
    } catch (e) {}
    setAiLoading(false);
  };

  const initEmbeddedOnboarding = useCallback(async (existingAccountId) => {
    setOnboardingLoading(true);
    try {
      const instance = loadConnectAndInitialize({
        publishableKey: isDemo ? PK_TEST : PK_LIVE,
        fetchClientSecret: async () => {
          // Build the body with all business form data
          const mcc = MCC_MAP[businessForm.industry] || MCC_MAP[(businessForm.industry || '').toLowerCase().replace(/\s+/g, '_')] || '1799';
          const stripeBizType = STRIPE_BUSINESS_TYPE_MAP[businessForm.businessType] || 'individual';

          const response = await fetch('/api/create-account-session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              companyId,
              email: businessForm.email,
              businessName: businessForm.businessName,
              description: businessForm.description,
              phone: businessForm.phone,
              url: businessForm.url,
              mcc,
              stripeBizType,
              street: businessForm.street,
              city: businessForm.city,
              state: businessForm.state,
              zip: businessForm.zip,
            })
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || 'Failed to create account session');
          }

          if (data.accountId && !existingAccountId) {
            setStripeStatus(prev => ({
              ...prev,
              connected: true,
              accountId: data.accountId
            }));
          }

          return data.client_secret;
        },
        appearance: {
          overlays: 'dialog',
          variables: {
            colorPrimary: '#F05A28',
            colorBackground: colors.bgInput,
            colorText: colors.text,
            colorDanger: '#e74c3c',
            borderRadius: '8px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }
        }
      });

      setConnectInstance(instance);
      setShowEmbeddedOnboarding(true);
      setOnboardingStep(3);
    } catch (error) {
      showError('Failed to start Stripe setup: ' + error.message);
    } finally {
      setOnboardingLoading(false);
      setConnecting(false);
    }
  }, [companyId, colors, isDemo, businessForm]); // eslint-disable-line

  const handleConnectStripe = async () => {
    setAcceptedTerms(false);
    setBusinessForm({ ...EMPTY_FORM });
    await prefillFormFromCompany();
    setOnboardingStep(1);
  };

  const handleCompleteSetup = async () => {
    setAcceptedTerms(false);
    setBusinessForm({ ...EMPTY_FORM });
    await prefillFormFromCompany();
    setOnboardingStep(1);
  };

  const handleContinueToReview = () => {
    // Basic validation
    if (!businessForm.businessName.trim()) {
      showError('Please enter your business name.');
      return;
    }
    if (!businessForm.description.trim()) {
      showError('Please enter a business description. You can use AI Autofill to generate one.');
      return;
    }
    setOnboardingStep(2);
  };

  const handleStartOnboarding = async () => {
    setConnecting(true);
    setOnboardingStep(3);
    await initEmbeddedOnboarding(stripeStatus.accountId || null);
  };

  const handleCloseOnboarding = () => {
    setOnboardingStep(0);
    setShowEmbeddedOnboarding(false);
    setConnectInstance(null);
    setAcceptedTerms(false);
  };

  const handleOpenStripeDashboard = async () => {
    try {
      const response = await fetch('/api/stripe-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId: stripeStatus.accountId })
      });

      const data = await response.json();

      if (response.ok && data.url) {
        window.open(data.url, '_blank');
      } else {
        window.open('https://dashboard.stripe.com/', '_blank');
      }
    } catch (error) {
      window.open('https://dashboard.stripe.com/', '_blank');
    }
  };

  const handleOnboardingExit = async () => {
    setShowEmbeddedOnboarding(false);
    setOnboardingStep(0);
    setConnectInstance(null);
    try {
      const resp = await fetch('/api/create-account-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId, action: 'sync-status' })
      });
      const data = await resp.json();
      if (data.connected) {
        setStripeStatus(prev => ({
          ...prev,
          chargesEnabled: data.chargesEnabled,
          payoutsEnabled: data.payoutsEnabled,
          detailsSubmitted: data.detailsSubmitted
        }));
      }
    } catch (e) {
      loadStripeStatus();
    }
  };

  // ---- Square Handlers ----
  const handleConnectSquare = async () => {
    try {
      const resp = await fetch(`/api/square-oauth?action=authorize&companyId=${companyId}`);
      const data = await resp.json();
      if (!resp.ok) {
        showError(data.error || 'Failed to start Square connection');
        return;
      }
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      showError('Failed to start Square connection');
    }
  };

  const handleDisconnectSquare = async () => {
    if (!window.confirm('Disconnect Square? You will no longer be able to accept payments through Square until you reconnect.')) return;
    try {
      const resp = await fetch('/api/square-oauth?action=disconnect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) {
        showError(data.error || 'Failed to disconnect Square');
        return;
      }
      setSquareStatus({ connected: false, merchantId: null, businessName: null, locationId: null, connectedAt: null });
      showSuccess('Square disconnected');
    } catch (err) {
      showError('Failed to disconnect Square');
    }
  };

  const handleProcessingFeeToggle = async (enabled) => {
    try {
      const { error } = await supabaseData
        .from('companies')
        .update({ processing_fee_enabled: enabled })
        .eq('id', companyId);

      if (error) throw error;

      setProcessingFeeEnabled(enabled);
      showSuccess(enabled ? 'Processing fee pass-through enabled' : 'Processing fee pass-through disabled');
    } catch (error) {
      showError('Failed to update setting');
    }
  };

  const handleFeePercentChange = async (percent) => {
    const value = parseFloat(percent);
    if (isNaN(value) || value < 0 || value > 10) return;

    setDefaultFeePercent(value);

    try {
      const { error } = await supabaseData
        .from('companies')
        .update({ default_processing_fee_percent: value })
        .eq('id', companyId);

      if (error) throw error;
    } catch (error) {
    }
  };

  const updateForm = (field, value) => {
    setBusinessForm(prev => ({ ...prev, [field]: value }));
  };

  // Shared input style
  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    background: colors.bgInput,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    color: colors.text,
    fontSize: '0.95rem',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    color: colors.textMuted,
    fontSize: '0.85rem',
    marginBottom: '0.35rem',
    fontWeight: '500',
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>
        <Loader size={24} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
        <div style={{ marginTop: '0.5rem' }}>Loading payment settings...</div>
      </div>
    );
  }

  // ---- Onboarding Modal (Steps 1-3) ----
  const renderOnboardingModal = () => {
    if (onboardingStep === 0) return null;

    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: isMobile ? colors.bg : 'rgba(0,0,0,0.85)',
        zIndex: 5000,
        display: 'flex',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'center',
        padding: isMobile ? '0' : '1rem',
        overflow: 'auto'
      }}>
        <div style={{
          background: colors.bgCard,
          borderRadius: isMobile ? '0' : '16px',
          width: isMobile ? '100%' : '100%',
          maxWidth: onboardingStep === 3 ? '700px' : '560px',
          height: isMobile ? '100%' : 'auto',
          maxHeight: isMobile ? '100%' : '95vh',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            padding: '1rem 1.5rem',
            paddingTop: isMobile ? 'max(1rem, env(safe-area-inset-top))' : '1rem',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {onboardingStep > 1 && onboardingStep < 3 && (
                <button
                  onClick={() => setOnboardingStep(onboardingStep - 1)}
                  style={{
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    padding: '0.4rem 0.75rem',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem'
                  }}
                >
                  <ChevronLeft size={16} /> Back
                </button>
              )}
              {onboardingStep === 3 && (
                <button
                  onClick={handleOnboardingExit}
                  style={{
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    padding: '0.4rem 0.75rem',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem'
                  }}
                >
                  <ChevronLeft size={16} /> Back
                </button>
              )}
              <h3 style={{ margin: 0, color: colors.text, fontSize: '1rem' }}>
                {onboardingStep === 1 && 'Business Information'}
                {onboardingStep === 2 && 'Review & Confirm'}
                {onboardingStep === 3 && 'Stripe Setup'}
              </h3>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {/* Step indicator */}
              <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                {[1, 2, 3].map(s => (
                  <div key={s} style={{
                    width: s === onboardingStep ? '24px' : '8px',
                    height: '8px',
                    borderRadius: '4px',
                    background: s <= onboardingStep ? colors.accent : colors.bgHover,
                    transition: 'all 0.2s'
                  }} />
                ))}
              </div>
              <button
                onClick={onboardingStep === 3 ? handleOnboardingExit : handleCloseOnboarding}
                style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', padding: '0.5rem' }}
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: '1.5rem', flex: 1, overflow: 'auto' }}>
            {onboardingStep === 1 && renderStep1()}
            {onboardingStep === 2 && renderStep2()}
            {onboardingStep === 3 && renderStep3()}
          </div>
        </div>
      </div>
    );
  };

  // ---- Step 1: Business Info Form ----
  const renderStep1 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* AI Autofill Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #8b5cf620, #a855f720)',
        border: '1px solid #a855f740',
        borderRadius: '10px',
        padding: '1rem 1.25rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        flexWrap: 'wrap'
      }}>
        <div>
          <div style={{ fontSize: '0.95rem', fontWeight: '600', color: colors.text, marginBottom: '0.2rem' }}>
            Auto-fill from your account
          </div>
          <div style={{ fontSize: '0.8rem', color: colors.textMuted }}>
            Pre-fills all fields using your company data + AI-generated description
          </div>
        </div>
        <button
          onClick={handleAiAutofill}
          disabled={aiLoading}
          style={{
            padding: '0.6rem 1.25rem',
            background: aiLoading ? colors.bgHover : 'linear-gradient(135deg, #8b5cf6, #a855f7)',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: aiLoading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexShrink: 0,
            whiteSpace: 'nowrap'
          }}
        >
          {aiLoading ? (
            <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Generating...</>
          ) : (
            <><Sparkles size={16} /> AI Autofill</>
          )}
        </button>
      </div>

      {/* Business Name */}
      <div>
        <label style={labelStyle}>Business Name *</label>
        <div style={{ position: 'relative' }}>
          <Building2 size={16} color={colors.textMuted} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            value={businessForm.businessName}
            onChange={e => updateForm('businessName', e.target.value)}
            placeholder="Your Business Name"
            style={{ ...inputStyle, paddingLeft: '2.5rem' }}
          />
        </div>
      </div>

      {/* Business Type + Industry row */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
        <div>
          <label style={labelStyle}>Business Type *</label>
          <select
            value={businessForm.businessType}
            onChange={e => updateForm('businessType', e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            {BUSINESS_TYPE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Industry / Category</label>
          <input
            type="text"
            value={businessForm.industry}
            onChange={e => updateForm('industry', e.target.value)}
            placeholder="e.g. garage_door, hvac, plumbing"
            style={inputStyle}
          />
        </div>
      </div>

      {/* Address */}
      <div>
        <label style={labelStyle}>Business Address</label>
        <div style={{ position: 'relative' }}>
          <MapPin size={16} color={colors.textMuted} style={{ position: 'absolute', left: '0.75rem', top: '0.75rem' }} />
          <input
            type="text"
            value={businessForm.street}
            onChange={e => updateForm('street', e.target.value)}
            placeholder="Street address"
            style={{ ...inputStyle, paddingLeft: '2.5rem' }}
          />
        </div>
      </div>

      {/* City / State / Zip */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', gap: '0.75rem' }}>
        <div>
          <label style={labelStyle}>City</label>
          <input
            type="text"
            value={businessForm.city}
            onChange={e => updateForm('city', e.target.value)}
            placeholder="City"
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>State</label>
          <select
            value={businessForm.state}
            onChange={e => updateForm('state', e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            <option value="">--</option>
            {US_STATES.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>ZIP</label>
          <input
            type="text"
            value={businessForm.zip}
            onChange={e => updateForm('zip', e.target.value)}
            placeholder="ZIP"
            style={inputStyle}
          />
        </div>
      </div>

      {/* Phone / Email row */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
        <div>
          <label style={labelStyle}>Business Phone</label>
          <div style={{ position: 'relative' }}>
            <Phone size={16} color={colors.textMuted} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="tel"
              value={businessForm.phone}
              onChange={e => updateForm('phone', e.target.value)}
              placeholder="(555) 555-5555"
              style={{ ...inputStyle, paddingLeft: '2.5rem' }}
            />
          </div>
        </div>
        <div>
          <label style={labelStyle}>Business Email</label>
          <div style={{ position: 'relative' }}>
            <Mail size={16} color={colors.textMuted} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="email"
              value={businessForm.email}
              onChange={e => updateForm('email', e.target.value)}
              placeholder="you@company.com"
              style={{ ...inputStyle, paddingLeft: '2.5rem' }}
            />
          </div>
        </div>
      </div>

      {/* Website */}
      <div>
        <label style={labelStyle}>Website URL</label>
        <div style={{ position: 'relative' }}>
          <Globe size={16} color={colors.textMuted} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="url"
            value={businessForm.url}
            onChange={e => updateForm('url', e.target.value)}
            placeholder="https://workvanapp.com"
            style={{ ...inputStyle, paddingLeft: '2.5rem' }}
          />
        </div>
      </div>

      {/* Description */}
      <div>
        <label style={labelStyle}>Business Description * <span style={{ fontWeight: '400', color: colors.textMuted }}>(2-3 sentences for Stripe)</span></label>
        <div style={{ position: 'relative' }}>
          <FileText size={16} color={colors.textMuted} style={{ position: 'absolute', left: '0.75rem', top: '0.75rem' }} />
          <textarea
            value={businessForm.description}
            onChange={e => updateForm('description', e.target.value)}
            placeholder="Describe what your business does, the services you provide, and the area you serve..."
            rows={3}
            style={{ ...inputStyle, paddingLeft: '2.5rem', resize: 'vertical', minHeight: '80px' }}
          />
        </div>
      </div>

      {/* Continue */}
      <button
        onClick={handleContinueToReview}
        style={{
          width: '100%',
          padding: '1rem',
          background: '#635bff',
          border: 'none',
          borderRadius: '10px',
          color: '#fff',
          fontSize: '1rem',
          fontWeight: '700',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          marginTop: '0.5rem'
        }}
      >
        Continue to Review <ArrowRight size={18} />
      </button>
    </div>
  );

  // ---- Step 2: Review & Confirm ----
  const renderStep2 = () => {
    const bizLabel = BUSINESS_TYPE_OPTIONS.find(o => o.value === businessForm.businessType)?.label || businessForm.businessType;
    const addressParts = [businessForm.street, businessForm.city, businessForm.state, businessForm.zip].filter(Boolean);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {/* Summary card */}
        <div style={{
          background: colors.bgInput,
          borderRadius: '12px',
          padding: '1.25rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem'
        }}>
          <h4 style={{ margin: 0, color: colors.text, fontSize: '1.05rem' }}>Review Your Business Info</h4>

          <SummaryRow label="Business Name" value={businessForm.businessName} colors={colors} />
          <SummaryRow label="Business Type" value={bizLabel} colors={colors} />
          {businessForm.industry && <SummaryRow label="Industry" value={businessForm.industry} colors={colors} />}
          {addressParts.length > 0 && <SummaryRow label="Address" value={addressParts.join(', ')} colors={colors} />}
          {businessForm.phone && <SummaryRow label="Phone" value={businessForm.phone} colors={colors} />}
          {businessForm.email && <SummaryRow label="Email" value={businessForm.email} colors={colors} />}
          {businessForm.url && <SummaryRow label="Website" value={businessForm.url} colors={colors} />}
          {businessForm.description && (
            <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '0.75rem', marginTop: '0.25rem' }}>
              <div style={{ fontSize: '0.8rem', color: colors.textMuted, marginBottom: '0.35rem' }}>Description</div>
              <div style={{ fontSize: '0.9rem', color: colors.text, lineHeight: 1.5 }}>{businessForm.description}</div>
            </div>
          )}

          <button
            onClick={() => setOnboardingStep(1)}
            style={{
              alignSelf: 'flex-start',
              background: 'none',
              border: 'none',
              color: colors.accent,
              fontSize: '0.85rem',
              cursor: 'pointer',
              padding: '0.25rem 0',
              fontWeight: '600',
              marginTop: '0.25rem'
            }}
          >
            Edit Info
          </button>
        </div>

        {/* Security note */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
          padding: '1rem', background: `${colors.success}10`,
          borderRadius: '10px', border: `1px solid ${colors.success}30`
        }}>
          <Sparkles size={20} color={colors.success} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ fontSize: '0.85rem', color: colors.textSecondary, lineHeight: 1.5 }}>
            <strong style={{ color: colors.text }}>Next: Bank & Identity Verification.</strong>{' '}
            Stripe will ask for your bank account details and identity verification. This is handled securely by Stripe -- Work Van never sees your banking or identity data.
          </div>
        </div>

        {/* T&C checkbox */}
        <div
          onClick={() => setAcceptedTerms(!acceptedTerms)}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem',
            cursor: 'pointer',
            padding: '0.75rem',
            borderRadius: '8px',
            background: acceptedTerms ? `${colors.accent}10` : 'transparent',
            border: `1px solid ${acceptedTerms ? colors.accent : colors.border}`,
            transition: 'all 0.15s'
          }}
        >
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '6px',
            border: `2px solid ${acceptedTerms ? colors.accent : colors.textMuted}`,
            background: acceptedTerms ? colors.accent : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            marginTop: '1px',
            transition: 'all 0.15s'
          }}>
            {acceptedTerms && (
              <CheckCircle size={16} color="#fff" />
            )}
          </div>
          <span style={{ fontSize: '0.85rem', color: colors.textSecondary, lineHeight: 1.5 }}>
            I understand I'll be completing Stripe's secure onboarding process, including agreeing to <a href="https://stripe.com/legal/connect-account" target="_blank" rel="noopener noreferrer" style={{ color: colors.accent }} onClick={(e) => e.stopPropagation()}>Stripe's Connected Account Agreement</a> and <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: colors.accent }} onClick={(e) => e.stopPropagation()}>Privacy Policy</a>.
          </span>
        </div>

        {/* Continue button */}
        <button
          onClick={handleStartOnboarding}
          disabled={!acceptedTerms || connecting || onboardingLoading}
          style={{
            width: '100%',
            padding: '1rem',
            background: (!acceptedTerms || connecting || onboardingLoading) ? colors.bgHover : '#635bff',
            border: 'none',
            borderRadius: '10px',
            color: (!acceptedTerms || connecting || onboardingLoading) ? colors.textMuted : '#fff',
            fontSize: '1rem',
            fontWeight: '700',
            cursor: (!acceptedTerms || connecting || onboardingLoading) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s'
          }}
        >
          {(connecting || onboardingLoading) ? (
            <><Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> Setting up...</>
          ) : (
            <><CreditCard size={18} /> Continue to Stripe Setup</>
          )}
        </button>
      </div>
    );
  };

  // ---- Step 3: Stripe Embedded Onboarding ----
  const renderStep3 = () => {
    if (!connectInstance) {
      return (
        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: colors.textMuted }}>
          <Loader size={28} style={{ animation: 'spin 1s linear infinite', marginBottom: '1rem' }} />
          <div>Loading Stripe onboarding...</div>
        </div>
      );
    }

    return (
      <div>
        <div style={{
          background: `${colors.success}10`,
          border: `1px solid ${colors.success}30`,
          borderRadius: '10px',
          padding: '0.75rem 1rem',
          marginBottom: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <CheckCircle size={16} color={colors.success} />
          <span style={{ fontSize: '0.85rem', color: colors.success, fontWeight: '600' }}>
            Business details pre-filled -- just verify your bank account and identity below
          </span>
        </div>

        <ConnectComponentsProvider connectInstance={connectInstance}>
          <ConnectAccountOnboarding
            onExit={handleOnboardingExit}
          />
        </ConnectComponentsProvider>
      </div>
    );
  };

  return (
    <div>
      <h3 style={{ fontSize: '1.5rem', margin: '0 0 1.5rem 0', color: colors.text }}>
        Payment Processing
      </h3>

      {isDemo && (
        <div style={{
          background: '#f59e0b20',
          border: '1px solid #f59e0b40',
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '0.85rem',
          color: '#f59e0b',
          fontWeight: '600'
        }}>
          <AlertCircle size={16} />
          Test Mode — Using Stripe test keys. No real charges will be made.
        </div>
      )}

      {/* Connection Status Card */}
      <div style={{
        background: colors.bgInput,
        padding: '1.5rem',
        borderRadius: '12px',
        marginBottom: '1.5rem',
        border: stripeStatus.connected ? '1px solid #4ecca3' : `1px solid ${colors.border}`
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: stripeStatus.connected ? '#4ecca320' : colors.bgHover,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <CreditCard size={24} color={stripeStatus.connected ? '#4ecca3' : colors.textMuted} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: '1.1rem',
              fontWeight: 'bold',
              color: colors.text,
              marginBottom: '0.25rem'
            }}>
              Stripe Connect
            </div>
            <div style={{
              fontSize: '0.9rem',
              color: stripeStatus.connected ? '#4ecca3' : colors.textMuted,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              {stripeStatus.connected ? (
                <>
                  <CheckCircle size={16} />
                  Connected
                </>
              ) : (
                <>
                  <AlertCircle size={16} />
                  Not connected
                </>
              )}
            </div>
          </div>
        </div>

        {stripeStatus.connected && onboardingStep === 0 ? (
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {stripeStatus.detailsSubmitted ? (
              <button
                onClick={handleOpenStripeDashboard}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#635bff',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <ExternalLink size={18} />
                Open Stripe Dashboard
              </button>
            ) : (
              <>
                <p style={{ color: colors.warning, fontSize: '0.9rem', marginBottom: '0.5rem', width: '100%' }}>
                  <AlertCircle size={16} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
                  Your Stripe setup is incomplete. Please finish to start accepting payments.
                </p>
                <button
                  onClick={handleCompleteSetup}
                  disabled={connecting || onboardingLoading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: (connecting || onboardingLoading) ? colors.bgHover : '#635bff',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontWeight: 'bold',
                    cursor: (connecting || onboardingLoading) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {(connecting || onboardingLoading) ? (
                    <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <CreditCard size={18} />
                  )}
                  Complete Stripe Setup
                </button>
              </>
            )}
          </div>
        ) : onboardingStep === 0 ? (
          <div>
            <p style={{ color: colors.textMuted, marginBottom: '1rem', fontSize: '0.9rem' }}>
              Connect your Stripe account to accept online payments from customers.
              Payments are deposited directly to your bank account.
            </p>
            <button
              onClick={handleConnectStripe}
              disabled={connecting || onboardingLoading}
              style={{
                padding: '0.75rem 1.5rem',
                background: (connecting || onboardingLoading) ? colors.bgHover : '#635bff',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontWeight: 'bold',
                cursor: (connecting || onboardingLoading) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {(connecting || onboardingLoading) ? (
                <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <CreditCard size={18} />
              )}
              {(connecting || onboardingLoading) ? 'Setting up...' : 'Connect / Sign Up for Stripe'}
            </button>
          </div>
        ) : null}
      </div>

      {/* Onboarding Modal (Steps 1-3) */}
      {renderOnboardingModal()}

      {/* Status Checklist (when connected) */}
      {stripeStatus.connected && (
        <div style={{
          background: colors.bgInput,
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '1.5rem'
        }}>
          <h4 style={{ margin: '0 0 1rem 0', color: colors.text }}>
            Account Status
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <StatusItem
              label="Account details submitted"
              completed={stripeStatus.detailsSubmitted}
            />
            <StatusItem
              label="Charges enabled"
              completed={stripeStatus.chargesEnabled}
              description="Can accept payments"
            />
            <StatusItem
              label="Payouts enabled"
              completed={stripeStatus.payoutsEnabled}
              description="Can receive deposits"
            />
          </div>

          {(!stripeStatus.chargesEnabled || !stripeStatus.payoutsEnabled) && onboardingStep === 0 && (
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              background: '#F05A2820',
              border: '1px solid #F05A2850',
              borderRadius: '8px',
              color: colors.accent,
              fontSize: '0.9rem'
            }}>
              Complete your Stripe account setup to start accepting payments.
              <button
                onClick={handleCompleteSetup}
                disabled={connecting || onboardingLoading}
                style={{
                  marginLeft: '0.5rem',
                  background: 'none',
                  border: 'none',
                  color: colors.accent,
                  textDecoration: 'underline',
                  cursor: (connecting || onboardingLoading) ? 'not-allowed' : 'pointer'
                }}
              >
                {(connecting || onboardingLoading) ? 'Loading...' : 'Complete Setup'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Payment Stats (when connected and has payments) */}
      {stripeStatus.connected && stripeStatus.chargesEnabled && (
        <div style={{
          background: colors.bgInput,
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '1.5rem'
        }}>
          <h4 style={{ margin: '0 0 1rem 0', color: colors.text }}>
            Payment Summary
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '1rem'
          }}>
            <StatCard
              label="Total Collected"
              value={`$${(stats.totalCollected / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
              color="#4ecca3"
            />
            <StatCard
              label="Platform Fees"
              value={`$${(stats.platformFees / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
              color="#888"
              small
            />
            <StatCard
              label="Net Received"
              value={`$${((stats.totalCollected - stats.platformFees) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
              color="#3498db"
            />
          </div>
        </div>
      )}

      {/* Card Readers / Terminal Section (when Stripe connected) */}
      {stripeStatus.connected && stripeStatus.chargesEnabled && (
        <div style={{
          background: colors.bgInput,
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '1.5rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1rem'
          }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              background: '#4ecca320',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Smartphone size={20} color="#4ecca3" />
            </div>
            <div>
              <h4 style={{ margin: 0, color: colors.text }}>
                Card Readers (Terminal)
              </h4>
              <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                Accept in-person card payments
              </div>
            </div>
          </div>
          <ReaderManagement companyId={companyId} />
        </div>
      )}

      {/* Processing Fee Pass-Through (when Stripe connected) */}
      {stripeStatus.connected && stripeStatus.chargesEnabled && (
        <div style={{
          background: colors.bgInput,
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '1.5rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1rem'
          }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              background: '#f39c1220',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CreditCard size={20} color="#f39c12" />
            </div>
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: 0, color: colors.text }}>
                Pass Processing Fees to Customers
              </h4>
              <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                Add a convenience fee to cover card processing costs
              </div>
            </div>
            <button
              onClick={() => handleProcessingFeeToggle(!processingFeeEnabled)}
              style={{
                width: '52px',
                height: '28px',
                borderRadius: '14px',
                background: processingFeeEnabled ? '#4ecca3' : colors.bgHover,
                border: 'none',
                cursor: 'pointer',
                position: 'relative',
                transition: 'background 0.2s',
              }}
            >
              <div
                style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  background: '#fff',
                  position: 'absolute',
                  top: '3px',
                  left: processingFeeEnabled ? '27px' : '3px',
                  transition: 'left 0.2s',
                }}
              />
            </button>
          </div>

          {processingFeeEnabled && (
            <>
              <div style={{
                background: colors.bgCard,
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                  Default Fee Percentage
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="0.25"
                    value={defaultFeePercent}
                    onChange={(e) => handleFeePercentChange(e.target.value)}
                    style={{
                      width: '80px',
                      padding: '0.5rem 0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text,
                      fontSize: '1rem',
                      textAlign: 'center'
                    }}
                  />
                  <span style={{ color: colors.text, fontWeight: 'bold' }}>%</span>
                  <span style={{ color: colors.textMuted, fontSize: '0.85rem', marginLeft: '0.5rem' }}>
                    (typically 2.5% - 3.5%)
                  </span>
                </div>
              </div>

              <div style={{
                padding: '1rem',
                background: '#1a3a2a',
                border: '1px solid #4ecca350',
                borderRadius: '8px',
                fontSize: '0.85rem'
              }}>
                <div style={{ color: '#4ecca3', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  How it works:
                </div>
                <ul style={{ margin: 0, paddingLeft: '1.25rem', color: colors.textMuted, lineHeight: 1.6 }}>
                  <li>When creating an invoice, you'll see an option to add the processing fee</li>
                  <li>The fee is shown as a separate "Card Processing Fee" line item</li>
                  <li>Customers see the fee before paying</li>
                  <li>You can choose per-invoice whether to apply the fee</li>
                </ul>
              </div>

              <div style={{
                marginTop: '1rem',
                padding: '0.75rem 1rem',
                background: '#F05A2820',
                border: '1px solid #F05A2850',
                borderRadius: '8px',
                fontSize: '0.8rem',
                color: colors.accent
              }}>
                <strong>Note:</strong> Surcharging laws vary by state. Some states (CT, MA, ME, OK) prohibit credit card surcharges.
                Ensure you comply with local regulations and card network rules.
              </div>
            </>
          )}
        </div>
      )}

      {/* ---- Square Section ---- */}
      <div style={{
        background: colors.bgInput,
        padding: '1.5rem',
        borderRadius: '12px',
        marginBottom: '1.5rem',
        border: squareStatus.connected ? '1px solid #006AFF' : `1px solid ${colors.border}`
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: squareStatus.connected ? '#006AFF20' : colors.bgHover,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Square logo — simplified inline SVG */}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="18" height="18" rx="3" fill={squareStatus.connected ? '#006AFF' : (colors.textMuted)} />
              <rect x="8" y="8" width="8" height="8" rx="1" fill={squareStatus.connected ? '#fff' : (colors.bgInput)} />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: '1.1rem',
              fontWeight: 'bold',
              color: colors.text,
              marginBottom: '0.25rem'
            }}>
              Square
            </div>
            <div style={{
              fontSize: '0.9rem',
              color: squareStatus.connected ? '#006AFF' : colors.textMuted,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              {squareStatus.connected ? (
                <>
                  <CheckCircle size={16} />
                  Connected
                  {squareStatus.merchantId && (
                    <span style={{ fontSize: '0.8rem', color: colors.textMuted }}>
                      ({squareStatus.merchantId})
                    </span>
                  )}
                </>
              ) : (
                <span style={{
                  padding: '0.2rem 0.55rem',
                  borderRadius: '999px',
                  background: '#F59E0B20',
                  color: '#F59E0B',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                }}>
                  Coming Soon
                </span>
              )}
            </div>
          </div>
          {/* Active processor badges */}
          {(paymentProcessor === 'both' || paymentProcessor === 'stripe' || paymentProcessor === 'square') && (
            <div style={{
              display: 'flex',
              gap: '0.4rem',
              flexWrap: 'wrap',
            }}>
              {(paymentProcessor === 'stripe' || paymentProcessor === 'both') && (
                <span style={{
                  padding: '0.25rem 0.6rem',
                  borderRadius: '999px',
                  background: '#635bff20',
                  color: '#635bff',
                  fontSize: '0.7rem',
                  fontWeight: '700',
                  letterSpacing: '0.02em',
                }}>
                  Stripe
                </span>
              )}
              {(paymentProcessor === 'square' || paymentProcessor === 'both') && (
                <span style={{
                  padding: '0.25rem 0.6rem',
                  borderRadius: '999px',
                  background: '#006AFF20',
                  color: '#006AFF',
                  fontSize: '0.7rem',
                  fontWeight: '700',
                  letterSpacing: '0.02em',
                }}>
                  Square
                </span>
              )}
            </div>
          )}
        </div>

        {squareStatus.connected ? (
          <div>
            {/* Connected state — show merchant info and disconnect */}
            <div style={{
              background: colors.bgCard,
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
            }}>
              {squareStatus.businessName && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.85rem', color: colors.textMuted }}>Business</span>
                  <span style={{ fontSize: '0.9rem', color: colors.text, fontWeight: '500' }}>{squareStatus.businessName}</span>
                </div>
              )}
              {squareStatus.merchantId && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.85rem', color: colors.textMuted }}>Merchant ID</span>
                  <span style={{ fontSize: '0.9rem', color: colors.text, fontWeight: '500', fontFamily: 'monospace' }}>{squareStatus.merchantId}</span>
                </div>
              )}
              {squareStatus.locationId && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.85rem', color: colors.textMuted }}>Location ID</span>
                  <span style={{ fontSize: '0.9rem', color: colors.text, fontWeight: '500', fontFamily: 'monospace' }}>{squareStatus.locationId}</span>
                </div>
              )}
              {squareStatus.connectedAt && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.85rem', color: colors.textMuted }}>Connected</span>
                  <span style={{ fontSize: '0.9rem', color: colors.text, fontWeight: '500' }}>
                    {new Date(squareStatus.connectedAt).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => window.open('https://squareup.com/dashboard', '_blank')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#006AFF',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <ExternalLink size={18} />
                Open Square Dashboard
              </button>
              <button
                onClick={handleDisconnectSquare}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'transparent',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.textMuted,
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Unlink size={16} />
                Disconnect
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p style={{ color: colors.textMuted, marginBottom: '1rem', fontSize: '0.9rem' }}>
              Square integration is on the way — we're polishing the connect
              flow now. In the meantime, Stripe handles all card payments
              (online + in-person via Stripe Terminal).
            </p>
            <button
              disabled
              style={{
                padding: '0.75rem 1.5rem',
                background: colors.bgHover,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.textMuted,
                fontWeight: '600',
                cursor: 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                opacity: 0.7,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="3" fill={colors.textMuted} />
                <rect x="8" y="8" width="8" height="8" rx="1" fill={colors.bgInput} />
              </svg>
              Connect Square — Coming Soon
            </button>
          </div>
        )}
      </div>

      {/* How it works */}
      <div style={{
        background: colors.bgCard,
        padding: '1.5rem',
        borderRadius: '12px',
        border: `1px solid ${colors.border}`
      }}>
        <h4 style={{ margin: '0 0 1rem 0', color: colors.text }}>
          How Online Payments Work
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <HowItWorksStep
            number={1}
            title="Connect Stripe"
            description="Link your business bank account through Stripe's secure platform"
          />
          <HowItWorksStep
            number={2}
            title="Send Invoices"
            description="Invoices include a 'Pay Now' button for customers"
          />
          <HowItWorksStep
            number={3}
            title="Get Paid"
            description="Payments are deposited to your bank in 2 business days"
          />
        </div>

        <div style={{
          marginTop: '1.5rem',
          padding: '1rem',
          background: colors.bgInput,
          borderRadius: '8px',
          fontSize: '0.85rem',
          color: colors.textMuted
        }}>
          <strong style={{ color: colors.text }}>Fees:</strong>
          <div style={{ marginTop: '0.5rem' }}>
            <div style={{ marginBottom: '0.35rem' }}>
              <strong>Online Payments:</strong> Stripe 2.9% + $0.30 + {PLATFORM_FEE_PERCENT}% platform fee
            </div>
            <div style={{ marginBottom: '0.35rem' }}>
              <strong>Terminal / Tap to Pay:</strong> Stripe 2.7% + $0.05 + {PLATFORM_FEE_PERCENT}% platform fee
              <span style={{ color: '#4ecca3', marginLeft: '0.5rem' }}>(Lower Stripe fees!)</span>
            </div>
            <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <Sparkles size={14} color="#a78bfa" />
              <strong style={{ color: '#a78bfa' }}>Financing (Afterpay/Klarna/Affirm):</strong>
              <span> Stripe ~6% + {PLATFORM_FEE_PERCENT_BNPL}% platform fee</span>
            </div>
          </div>
          <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: colors.bgCard, borderRadius: '6px' }}>
            <div style={{ color: '#a78bfa', fontWeight: 'bold', marginBottom: '0.25rem' }}>
              Why offer financing?
            </div>
            <div style={{ lineHeight: 1.4 }}>
              Close more deals on big-ticket items! Customers pay in installments via Afterpay, Klarna, or Affirm.
              You get paid immediately - they handle the installments.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper Components
function SummaryRow({ label, value, colors }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem' }}>
      <span style={{ fontSize: '0.85rem', color: colors.textMuted, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '0.9rem', color: colors.text, fontWeight: '500', textAlign: 'right' }}>{value}</span>
    </div>
  );
}

function StatusItem({ label, completed, description }) {
  const { colors } = useTheme();
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    }}>
      <div style={{
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        background: completed ? '#4ecca3' : colors.bgHover,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {completed ? (
          <CheckCircle size={14} color={colors.bg} />
        ) : (
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: colors.textMuted
          }} />
        )}
      </div>
      <div>
        <div style={{ color: completed ? colors.text : colors.textMuted }}>
          {label}
        </div>
        {description && (
          <div style={{ fontSize: '0.8rem', color: colors.textMuted }}>
            {description}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color, small }) {
  const { colors } = useTheme();
  return (
    <div style={{
      background: colors.bgCard,
      padding: '1rem',
      borderRadius: '8px',
      textAlign: 'center'
    }}>
      <div style={{
        fontSize: small ? '1.25rem' : '1.5rem',
        fontWeight: 'bold',
        color: color
      }}>
        {value}
      </div>
      <div style={{ fontSize: '0.8rem', color: colors.textMuted, marginTop: '0.25rem' }}>
        {label}
      </div>
    </div>
  );
}

function HowItWorksStep({ number, title, description }) {
  const { colors } = useTheme();
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '1rem'
    }}>
      <div style={{
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        background: colors.accent,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 'bold',
        fontSize: '0.9rem',
        flexShrink: 0
      }}>
        {number}
      </div>
      <div>
        <div style={{ color: colors.text, fontWeight: 'bold' }}>
          {title}
        </div>
        <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
          {description}
        </div>
      </div>
    </div>
  );
}
